import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api/client';
import { useIsSiteAdmin } from '../../lib/api/hooks';
import { printReceipt } from '../../lib/store/receipt';

interface OrderLine { productId: string; name: string; quantity: number; unitCents: number }
interface Tracking { carrier: string; trackingNumber: string; url?: string; shippedOn: string }
interface ShipAddress {
  name?: string; phone?: string; line1?: string; line2?: string;
  city?: string; state?: string; postalCode?: string; country?: string;
}
interface AdminOrder {
  orderNum: string;
  status: string;
  createdOn?: string;
  accountId: string;
  contactEmail?: string;
  items: OrderLine[];
  subtotalCents: number;
  shippingCents: number;
  totalCents: number;
  shippingCarrier?: string;
  shippingService?: string;
  shippingAccountNumber?: string;
  shippingAddress?: ShipAddress;
  tracking?: Tracking;
}

const money = (c: number) => `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const STATUS_TONE: Record<string, string> = {
  PENDING: 'pend', PAID: 'ok', FULFILLING: 'ok', SHIPPED: 'ship', DELIVERED: 'ship', CANCELLED: 'bad',
};
const TABS = ['ALL', 'PAID', 'FULFILLING', 'SHIPPED', 'DELIVERED', 'PENDING', 'CANCELLED'] as const;
type Tab = (typeof TABS)[number];
const CARRIERS = ['UPS', 'FedEx', 'USPS', 'DHL'];

function shipToLines(a?: ShipAddress): string[] {
  if (!a) return [];
  const cityStateZip = [[a.city, a.state].filter(Boolean).join(', '), a.postalCode].filter(Boolean).join(' ');
  return [a.name, a.line1, a.line2, cityStateZip, a.country || 'US', a.phone ? `☎ ${a.phone}` : '']
    .map((s) => (s ?? '').trim())
    .filter(Boolean);
}

export function AdminOrders() {
  const isAdmin = useIsSiteAdmin();
  const [tab, setTab] = useState<Tab>('ALL');
  const orders = useQuery({
    queryKey: ['admin-orders', tab],
    queryFn: () => api.get<AdminOrder[]>(`/admin/orders?status=${tab}`),
    enabled: isAdmin,
  });

  if (!isAdmin) return <div className="panel empty"><h3>Not authorized</h3></div>;

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Orders</h1>
          <div className="sub">Store order history, fulfillment status, and shipment tracking.</div>
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: 16 }}>
        {TABS.map((t) => (
          <button key={t} className={`tab${t === tab ? ' is-active' : ''}`} onClick={() => setTab(t)}>
            {t === 'ALL' ? 'All' : t.charAt(0) + t.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {orders.isLoading && <p className="loading-screen">// loading orders…</p>}
      {orders.data && orders.data.length === 0 && (
        <div className="panel empty"><h3>No orders</h3><p>No orders in this status.</p></div>
      )}

      <div className="orders">
        {orders.data?.map((o) => <AdminOrderCard key={o.orderNum} order={o} tab={tab} />)}
      </div>
    </div>
  );
}

function AdminOrderCard({ order: o, tab }: { order: AdminOrder; tab: Tab }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [carrier, setCarrier] = useState(o.shippingCarrier || 'FedEx');
  const [tracking, setTracking] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const ship = useMutation({
    mutationFn: () => api.post(`/admin/orders/${o.orderNum}/tracking`, { carrier, trackingNumber: tracking.trim() }),
    onSuccess: () => {
      setOpen(false); setTracking('');
      qc.invalidateQueries({ queryKey: ['admin-orders'] });
      qc.invalidateQueries({ queryKey: ['ops'] }); // keep the Operations pending queue in sync
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Failed to save tracking'),
  });

  const canShip = (o.status === 'PAID' || o.status === 'FULFILLING') && !o.tracking;
  const addr = shipToLines(o.shippingAddress);
  // A blocked pop-up leaves the screen unchanged, which reads as a broken button.
  const [blocked, setBlocked] = useState(false);

  return (
    <div className="order-card admin-order-card">
      <div className="order-top">
        <div>
          <span className="mono order-num">{o.orderNum}</span>
          {/* Packing slip. Sits on the order header rather than in the ship form so it can be printed at
              any status — picking a shipment is not the only reason to want the paperwork. */}
          <button
            className="btn btn-ghost btn-sm"
            title="Print the packing slip / receipt"
            onClick={() => { if (!printReceipt(o)) setBlocked(true); }}
          >Receipt</button>
          {o.createdOn && <span className="order-date">{new Date(o.createdOn).toLocaleDateString()}</span>}
        </div>
        <span className={`status-badge tone-${STATUS_TONE[o.status] ?? 'pend'}`}>{o.status}</span>
      </div>

      <div className="order-meta muted" style={{ margin: '2px 0 10px', fontSize: 13 }}>
        {o.contactEmail ?? o.accountId}
      </div>

      {blocked && <p className="cart-error">Allow pop-ups for this site to print the receipt.</p>}
      <ul className="order-items">
        {o.items.map((li, i) => (
          <li key={i}>
            <span>{li.quantity}× {li.name}</span>
            <span className="mono">{money(li.unitCents * li.quantity)}</span>
          </li>
        ))}
      </ul>

      <div className="order-totals" style={{ margin: '8px 0', fontSize: 13 }}>
        <div className="order-sum-row"><span className="muted">Subtotal</span><span className="mono">{money(o.subtotalCents)}</span></div>
        <div className="order-sum-row">
          <span className="muted">Shipping{o.shippingService ? ` · ${o.shippingService}` : ''}</span>
          <span className="mono">{money(o.shippingCents)}</span>
        </div>
        <div className="order-sum-row"><strong>Total</strong><strong className="mono">{money(o.totalCents)}</strong></div>
      </div>

      {(addr.length > 0 || o.shippingAccountNumber) && (
        <div className="order-ship" style={{ margin: '8px 0', fontSize: 13 }}>
          <div className="muted" style={{ marginBottom: 2 }}>Ship to</div>
          {addr.map((l, i) => <div key={i}>{l}</div>)}
          {o.shippingCarrier && o.shippingAccountNumber && (
            <div className="muted" style={{ marginTop: 4 }}>{o.shippingCarrier} acct #{o.shippingAccountNumber}</div>
          )}
        </div>
      )}

      <div className="order-bottom">
        {o.tracking ? (
          <span className="tracking">
            📦 {o.tracking.carrier} ·{' '}
            {o.tracking.url ? (
              <a href={o.tracking.url} target="_blank" rel="noreferrer" className="mono track-link">{o.tracking.trackingNumber}</a>
            ) : (
              <span className="mono">{o.tracking.trackingNumber}</span>
            )}
            {o.tracking.shippedOn && <span className="muted"> · {new Date(o.tracking.shippedOn).toLocaleDateString()}</span>}
          </span>
        ) : canShip ? (
          !open ? (
            <button className="btn btn-primary btn-sm" onClick={() => setOpen(true)}>Add tracking &amp; notify →</button>
          ) : (
            <div className="ops-ship-form">
              <select className="select" value={carrier} onChange={(e) => setCarrier(e.target.value)}>
                {CARRIERS.map((c) => <option key={c}>{c}</option>)}
              </select>
              <input className="input" placeholder="Tracking number" value={tracking} onChange={(e) => setTracking(e.target.value)} />
              <button className="btn btn-primary btn-sm" disabled={!tracking.trim() || ship.isPending}
                onClick={() => { setErr(null); ship.mutate(); }}>{ship.isPending ? 'Saving…' : 'Ship'}</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>Cancel</button>
              {err && <span className="ops-ship-err">{err}</span>}
            </div>
          )
        ) : (
          <span className="tracking muted">{o.status === 'PENDING' ? 'Awaiting payment' : 'No tracking'}</span>
        )}
      </div>
    </div>
  );
}
